import { useState, useCallback } from "react";

const STORAGE_PREFIX = "grid_prefs_";

function loadPreferences(gridId) {
  try {
    const saved = localStorage.getItem(STORAGE_PREFIX + gridId);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    localStorage.removeItem(STORAGE_PREFIX + gridId);
  }
  return null;
}

function savePreferences(gridId, prefs) {
  try {
    localStorage.setItem(STORAGE_PREFIX + gridId, JSON.stringify(prefs));
  } catch (e) {
    // localStorage full or unavailable — silently ignore
  }
}

export default function useGridPreferences(gridId, defaultColumns) {
  const [prefs, setPrefs] = useState(() => {
    const saved = loadPreferences(gridId);
    return {
      columnOrder: saved?.columnOrder || defaultColumns,
      columnWidths: saved?.columnWidths || {},
      hiddenColumns: saved?.hiddenColumns || [],
    };
  });

  const updatePrefs = useCallback((updater) => {
    setPrefs((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      savePreferences(gridId, next);
      return next;
    });
  }, [gridId]);

  const setColumnOrder = useCallback((order) => {
    updatePrefs((prev) => ({ ...prev, columnOrder: order }));
  }, [updatePrefs]);

  const setColumnWidth = useCallback((columnId, width) => {
    updatePrefs((prev) => ({
      ...prev,
      columnWidths: { ...prev.columnWidths, [columnId]: width },
    }));
  }, [updatePrefs]);

  const toggleColumnVisibility = useCallback((columnId) => {
    updatePrefs((prev) => {
      const hidden = prev.hiddenColumns.includes(columnId)
        ? prev.hiddenColumns.filter((c) => c !== columnId)
        : [...prev.hiddenColumns, columnId];
      return { ...prev, hiddenColumns: hidden };
    });
  }, [updatePrefs]);

  const resetPreferences = useCallback(() => {
    const defaults = {
      columnOrder: defaultColumns,
      columnWidths: {},
      hiddenColumns: [],
    };
    savePreferences(gridId, defaults);
    setPrefs(defaults);
  }, [gridId, defaultColumns]);

  return {
    ...prefs,
    setColumnOrder,
    setColumnWidth,
    toggleColumnVisibility,
    resetPreferences,
  };
}
